/**
 * The clock derived timeline engine.
 *
 * Nothing here increments a counter. Progress is a pure function of
 * (now - startedAt) * speed against the script's cumulative offsets, which
 * buys three things for free: a refresh mid build keeps the build correct,
 * a backgrounded tab does not desync (browsers throttle timers in background
 * tabs, which is exactly what breaks tick based fakes during a live demo),
 * and the speed multiplier is one number.
 */

import type { LogLine, StepStatus, Timeline, TimelineState } from '../types';
import { logsFor, scriptFor, stepOffsets } from './scripts';

const FAIL_AT: Record<string, string> = {
  'fail@build': 'build',
  'fail@database': 'database.create',
};

function scaledElapsed(timeline: Timeline, now: number): number {
  if (timeline.speed === 'instant') return Number.POSITIVE_INFINITY;
  return Math.max(0, (now - timeline.startedAt) * timeline.speed);
}

/** The step a given outcome stops on, or -1 when the run succeeds. */
function failIndex(timeline: Timeline): number {
  const key = FAIL_AT[timeline.outcome];
  if (!key) return -1;
  const script = scriptFor(timeline.scriptId);
  return script.steps.findIndex((step) => step.key === key);
}

export function stateOf(timeline: Timeline, now: number): TimelineState {
  const script = scriptFor(timeline.scriptId);
  const offsets = stepOffsets(script);
  const totalMs = script.totalMs;
  const stopAt = failIndex(timeline);
  const elapsedRaw = scaledElapsed(timeline, now);
  const ceiling = stopAt >= 0 ? offsets[stopAt] : totalMs;
  const elapsed = Math.min(elapsedRaw, ceiling);

  let stepIndex = offsets.findIndex((end) => elapsed < end);
  if (stepIndex === -1) stepIndex = script.steps.length - 1;

  const start = stepIndex === 0 ? 0 : offsets[stepIndex - 1];
  const span = offsets[stepIndex] - start;
  const stepProgress = span <= 0 ? 1 : Math.min(1, Math.max(0, (elapsed - start) / span));

  const done = elapsed >= ceiling;
  const failed = done && stopAt >= 0;
  const succeeded = done && stopAt < 0;

  const stepStatuses: StepStatus[] = script.steps.map((_, i) => {
    if (stopAt >= 0 && i > stopAt) return 'skipped';
    // The failing step is named before anything else is decided. When elapsed
    // lands exactly on the step's own end offset, stepIndex has already moved
    // past it, so the "i < stepIndex means succeeded" rule below would report
    // the step that broke the build as the step that finished it.
    if (failed && i === stopAt) return 'failed';
    if (i < stepIndex) return 'succeeded';
    if (i > stepIndex) return 'pending';
    if (failed) return 'failed';
    if (succeeded) return 'succeeded';
    return 'running';
  });

  // The log tail is read for the ending this run is having, so a run that
  // stopped on the build step never prints the line that says it compiled.
  const logsUpTo: LogLine[] = logsFor(script, timeline.outcome).filter(
    (log) => log.at <= elapsed,
  );

  const status = failed
    ? 'failed'
    : succeeded
      ? 'succeeded'
      : elapsed <= 0
        ? 'queued'
        : 'running';

  return {
    status,
    stepIndex,
    stepProgress,
    stepStatuses,
    logsUpTo,
    elapsedMs: Number.isFinite(elapsed) ? elapsed : ceiling,
    totalMs,
  };
}

/** True while a timeline still has work to render. Drives the one ticker. */
export function isLive(timeline: Timeline, now: number): boolean {
  const status = stateOf(timeline, now).status;
  return status === 'queued' || status === 'running';
}

export function anyLive(timelines: Timeline[], now: number): boolean {
  return timelines.some((timeline) => isLive(timeline, now));
}
